import React from "react"
import MyCard from "@common-utils/MyCard"
import MyTooltip from "@common-utils/MyTooltip"
import { Form, Formik } from "formik"
import { CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT } from "../../../../endpoints"
import { IGetViviersRequestModel } from "./core/_models"
import { useAuth } from "../../../../AppContext"
import { useViviersListingContext } from "./ViviersListing"
import { CONST_VIVIER_STATUS_DISPONIBLE, CONST_VIVIER_STATUS_OPTIONS_FOR_SELECT2 } from "@common-constants/*"

// 🔹 New Field Components
import MyInputFormField from "@common-components/fields/MyInputFormField"
import MySelectGetAllDataFromServerFormField from "@common-components/fields/MySelectGetAllDataFromServerFormField"
import MySelectFormField from "@common-components/fields/MySelectFormField"

const ViviersListingFilters = () => {
     const context = useViviersListingContext()
     const { currentUser } = useAuth()

     const initialValues: IGetViviersRequestModel = {
          ...context.filters,
     }

     function handleSubmit(values: IGetViviersRequestModel) {
          context.setFilters(prev => ({
               ...prev,
               ...values,
          }))
          context.setAreFiltersVisible(false)
          context.setItemInPreview(null)
     }

     function handleReset() {
          // 1. Define Defaults (Matching ViviersListing.tsx defaults)
          const defaultFilters = {
               page: 1,
               pageLength: 20,
               responsables: currentUser?.teamRecrutement?.map(item => item.id as number) || [],
               keyword: "",
               // 🔹 Default status is DISPONIBLE
               statuses: [CONST_VIVIER_STATUS_DISPONIBLE],
          }

          // 2. Update Global State
          context.setFilters(defaultFilters)
          context.setItemInPreview(null)
          context.setAreFiltersVisible(false)

          // 3. Reset Local Form
          if (context.filtersFormikRef.current) {
               context.filtersFormikRef.current.resetForm({ values: defaultFilters })
               context.filtersFormikRef.current.setSubmitting(true)
          }

          // 4. Reset Search Bar
          if (context.simpleSearchBarFormikRef.current) {
               context.simpleSearchBarFormikRef.current.resetForm({ values: { keyword: "" } })
          }
     }

     function handleClickHideFilters() {
          context.setAreFiltersVisible(prev => !prev)
          // Re-open preview if it was hidden when filters appeared
          context.setItemInPreview(prev => {
               if (prev) return { ...prev, isPreviewVisible: true }
               return null
          })
     }

     const layoutProps = {
          classNameLayout: "mb-5",
          classNameLabel: "col-12 col-form-label fw-bold fs-6",
          classNameField: "col-12",
     }

     return (
          <MyCard>
               <MyCard.Header
                    isCollapseIconShown={false}
                    rightSideContent={
                         <MyTooltip title={"Cacher les filtres"}>
                              <button className={"btn btn-sm btn-outline btn-outline-secondary "} onClick={handleClickHideFilters}>
                                   <i className={"fas fa-eye-slash fs-4 text-dark"} />
                              </button>
                         </MyTooltip>
                    }
               >
                    Filtres
               </MyCard.Header>

               <MyCard.Body>
                    <Formik
                         initialValues={initialValues}
                         onSubmit={handleSubmit}
                         innerRef={context.filtersFormikRef}
                         enableReinitialize={true} // 🔹 Important for Reset
                    >
                         {formikProps => {
                              return (
                                   <Form noValidate autoComplete="off">
                                        <MyInputFormField
                                             label="Mot clé"
                                             name="keyword"
                                             placeholder="Rechercher ..."
                                             formikProps={formikProps}
                                             onChangeCallback={val => {
                                                  if (context.simpleSearchBarFormikRef.current) {
                                                       context.simpleSearchBarFormikRef.current.setFieldValue("keyword", val).then()
                                                  }
                                             }}
                                             {...layoutProps}
                                        />

                                        <div className="separator separator-dotted mt-4 mb-8"></div>

                                        <MySelectGetAllDataFromServerFormField
                                             label="Responsables"
                                             name="responsables"
                                             placeholder="Sélectionner des responsables"
                                             endpoint={CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT}
                                             method="GET"
                                             defaultOptions={
                                                  currentUser?.teamRecrutement && currentUser.teamRecrutement.length > 0
                                                       ? currentUser.teamRecrutement.map(item => ({
                                                              value: item.id as number,
                                                              label: item.prenomNom as string,
                                                         }))
                                                       : []
                                             }
                                             isMulti
                                             formikProps={formikProps}
                                             {...layoutProps}
                                        />

                                        <MySelectFormField
                                             label="Statuts"
                                             name="statuses"
                                             placeholder="Statuts"
                                             options={CONST_VIVIER_STATUS_OPTIONS_FOR_SELECT2}
                                             formikProps={formikProps}
                                             isMulti
                                             {...layoutProps}
                                        />

                                        <div className={"text-end mt-4"}>
                                             <button
                                                  className={"btn btn-sm btn-outline btn-outline-primary d-inline-flex align-items-center me-2"}
                                                  type={"button"}
                                                  disabled={formikProps.isSubmitting}
                                                  onClick={() => handleReset()}
                                             >
                                                  <span className={"d-none d-lg-block"}>Réinitialiser</span> <i className={"fas fa-sync ms-lg-1"} />
                                             </button>
                                             <button className={"btn btn-sm btn-primary d-inline-flex align-items-center"} type={"submit"} disabled={formikProps.isSubmitting}>
                                                  <span className={"d-none d-lg-block"}>Filtrer </span>
                                                  {!formikProps.isSubmitting ? (
                                                       <i className={"fas fa-filter ms-md-1"} />
                                                  ) : (
                                                       <span className="spinner-border spinner-border-sm align-middle ms-lg-1" />
                                                  )}
                                             </button>
                                        </div>
                                   </Form>
                              )
                         }}
                    </Formik>
               </MyCard.Body>
          </MyCard>
     )
}

export default ViviersListingFilters
